/**
 * Opdracht Detail Screen
 * Shows opdracht sections and allows users to complete them
 */

import { useState, useCallback } from "react";
import {
  View,
  Text,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  Alert,
  TextInput,
  RefreshControl,
} from "react-native";
import { Image } from "expo-image";
import { SafeAreaView } from "react-native-safe-area-context";
import { useLocalSearchParams, useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { format, formatDistanceToNow } from "date-fns";
import { nl } from "date-fns/locale";
import * as ImagePicker from "expo-image-picker";
import * as FileSystem from "expo-file-system/legacy";
import { decode } from "base64-arraybuffer";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/lib/supabase/client";
import {
  useOpdrachtDetail,
  SectionWithStatus,
  OpdrachtConfig,
  SECTION_ICONS,
  SECTION_LABELS,
  SECTION_COLORS,
} from "@/modules/challenges";

/**
 * Opdracht Detail Screen
 */
export default function OpdrachtDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const router = useRouter();
  const { isDark } = useTheme();
  const colors = useThemeColors();
  const { activeProfile } = useAuth();

  const { opdracht, isLoading, error, refetch, submitSection, updateSection } =
    useOpdrachtDetail(id);

  // Refresh state
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Handle pull to refresh
   */
  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
  }, [refetch]);

  // Section completion state
  const [activeSection, setActiveSection] = useState<SectionWithStatus | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Section-specific inputs
  const [textInput, setTextInput] = useState("");
  const [numberInput, setNumberInput] = useState("");
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  /**
   * Handle picking image for photo section
   */
  const handlePickImage = async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") {
      Alert.alert("Fout", "We hebben toegang tot je foto's nodig.");
      return;
    }

    const result = await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ImagePicker.MediaTypeOptions.Images,
      allowsEditing: true,
      aspect: [4, 3],
      quality: 0.8,
    });

    if (!result.canceled && result.assets[0]) {
      setSelectedImage(result.assets[0].uri);
    }
  };

  /**
   * Upload image to Supabase storage
   */
  const uploadImage = async (uri: string): Promise<string | null> => {
    try {
      const base64 = await FileSystem.readAsStringAsync(uri, {
        encoding: FileSystem.EncodingType.Base64,
      });
      const arrayBuffer = decode(base64);
      const fileName = `section_${activeSection?.id}_${Date.now()}.jpg`;
      const filePath = `sections/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from("challenge-submissions")
        .upload(filePath, arrayBuffer, {
          contentType: "image/jpeg",
          upsert: true,
        });

      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage
        .from("challenge-submissions")
        .getPublicUrl(filePath);

      return urlData.publicUrl;
    } catch (err) {
      console.error("Error uploading image:", err);
      return null;
    }
  };

  /**
   * Submit section completion
   */
  const handleSubmitSection = async () => {
    if (!activeSection) return;

    setIsSubmitting(true);

    try {
      let submission: Record<string, unknown> = {};

      switch (activeSection.section_type) {
        case "checkbox":
          submission = { completed: true };
          break;

        case "text":
          if (!textInput.trim()) {
            Alert.alert("Fout", "Vul een antwoord in.");
            setIsSubmitting(false);
            return;
          }
          submission = { answer: textInput.trim() };
          break;

        case "number":
          const numValue = parseFloat(numberInput);
          if (isNaN(numValue)) {
            Alert.alert("Fout", "Vul een geldig getal in.");
            setIsSubmitting(false);
            return;
          }
          submission = { value: numValue };
          break;

        case "photo":
          if (!selectedImage) {
            Alert.alert("Fout", "Selecteer een foto.");
            setIsSubmitting(false);
            return;
          }
          const photoUrl = await uploadImage(selectedImage);
          if (!photoUrl) {
            Alert.alert("Fout", "Kon foto niet uploaden.");
            setIsSubmitting(false);
            return;
          }
          submission = { photo_url: photoUrl };
          break;

        case "location":
          // TODO: Implement location check-in
          submission = { checked_in_at: { lat: 0, lng: 0 } };
          break;
      }

      const success = await submitSection(activeSection.id, submission);

      if (success) {
        // Reset state
        setActiveSection(null);
        setTextInput("");
        setNumberInput("");
        setSelectedImage(null);
        Alert.alert("Succes", "Sectie ingediend!");
      }
    } catch (err: any) {
      console.error("Error submitting section:", err);
      Alert.alert("Fout", err.message || "Kon sectie niet indienen.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Render section completion form
   */
  const renderSectionForm = () => {
    if (!activeSection) return null;

    return (
      <View
        className="absolute bottom-0 left-0 right-0 bg-white dark:bg-gray-900 rounded-t-3xl p-6 shadow-lg"
        style={{ backgroundColor: colors.surface }}
      >
        <View className="flex-row items-center justify-between mb-4">
          <Text className="text-lg font-bold" style={{ color: colors.text }}>
            {activeSection.title}
          </Text>
          <TouchableOpacity onPress={() => setActiveSection(null)}>
            <Ionicons name="close" size={24} color={colors.text} />
          </TouchableOpacity>
        </View>

        {activeSection.description && (
          <Text className="mb-4" style={{ color: colors.textSecondary }}>
            {activeSection.description}
          </Text>
        )}

        {/* Section type specific form */}
        {activeSection.section_type === "checkbox" && (
          <View className="items-center py-8">
            <Ionicons name="checkmark-circle" size={64} color={colors.success} />
            <Text className="mt-4 text-center" style={{ color: colors.textSecondary }}>
              Tik op "Voltooien" om deze taak als gedaan te markeren.
            </Text>
          </View>
        )}

        {activeSection.section_type === "text" && (
          <TextInput
            value={textInput}
            onChangeText={setTextInput}
            placeholder="Typ je antwoord..."
            placeholderTextColor={colors.textTertiary}
            className="p-4 rounded-xl text-base mb-4"
            style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
            multiline
            numberOfLines={4}
          />
        )}

        {activeSection.section_type === "number" && (
          <TextInput
            value={numberInput}
            onChangeText={setNumberInput}
            placeholder="Voer een getal in..."
            placeholderTextColor={colors.textTertiary}
            className="p-4 rounded-xl text-base mb-4"
            style={{ backgroundColor: colors.surfaceSecondary, color: colors.text }}
            keyboardType="numeric"
          />
        )}

        {activeSection.section_type === "photo" && (
          <View className="mb-4">
            {selectedImage ? (
              <View>
                <Image
                  source={{ uri: selectedImage }}
                  style={{ width: "100%", height: 200, borderRadius: 12 }}
                  contentFit="cover"
                />
                <TouchableOpacity
                  onPress={() => setSelectedImage(null)}
                  className="mt-2 py-2 items-center"
                >
                  <Text style={{ color: colors.error }}>Andere foto kiezen</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <TouchableOpacity
                onPress={handlePickImage}
                className="py-8 rounded-xl items-center"
                style={{ backgroundColor: colors.surfaceSecondary, borderWidth: 2, borderStyle: "dashed", borderColor: colors.border }}
              >
                <Ionicons name="camera" size={48} color={colors.textTertiary} />
                <Text className="mt-2" style={{ color: colors.textSecondary }}>
                  Tik om een foto te selecteren
                </Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {activeSection.section_type === "location" && (
          <View className="items-center py-8">
            <Ionicons name="location" size={64} color={colors.primary} />
            <Text className="mt-4 text-center" style={{ color: colors.textSecondary }}>
              Locatie check-in komt binnenkort...
            </Text>
          </View>
        )}

        {/* Points info */}
        <View className="flex-row items-center justify-center mb-4 py-2 rounded-lg" style={{ backgroundColor: colors.primaryLight }}>
          <Ionicons name="star" size={16} color={colors.primary} />
          <Text className="ml-2 font-medium" style={{ color: colors.primary }}>
            {activeSection.points} punten
          </Text>
        </View>

        {/* Submit button */}
        <TouchableOpacity
          onPress={handleSubmitSection}
          disabled={isSubmitting || activeSection.section_type === "location"}
          className="py-4 rounded-xl items-center"
          style={{
            backgroundColor: activeSection.section_type === "location" ? colors.surfaceSecondary : colors.success,
            opacity: isSubmitting ? 0.7 : 1,
          }}
        >
          {isSubmitting ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text className="font-bold" style={{ color: activeSection.section_type === "location" ? colors.textSecondary : "#FFFFFF" }}>
              {activeSection.section_type === "location" ? "Binnenkort beschikbaar" : "Voltooien"}
            </Text>
          )}
        </TouchableOpacity>
      </View>
    );
  };

  // Loading state
  if (isLoading) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </SafeAreaView>
    );
  }

  // Error state
  if (error || !opdracht) {
    return (
      <SafeAreaView className="flex-1 items-center justify-center p-6" style={{ backgroundColor: colors.background }} edges={["top", "left", "right"]}>
        <Ionicons name="alert-circle" size={64} color={colors.error} />
        <Text className="text-lg font-bold mt-4 text-center" style={{ color: colors.text }}>
          {error || "Opdracht niet gevonden"}
        </Text>
        <TouchableOpacity
          onPress={() => router.back()}
          className="mt-4 px-6 py-3 rounded-xl"
          style={{ backgroundColor: colors.primary }}
        >
          <Text className="text-white font-semibold">Terug</Text>
        </TouchableOpacity>
      </SafeAreaView>
    );
  }

  const config = opdracht.config as OpdrachtConfig;

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: colors.background }} edges={["top"]}>
      {/* Header */}
      <View
        className="flex-row items-center px-4 py-3 border-b"
        style={{ borderColor: colors.border }}
      >
        <TouchableOpacity
          onPress={() => router.back()}
          className="p-2 -ml-2"
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Ionicons name="arrow-back" size={24} color={colors.text} />
        </TouchableOpacity>
        <Text
          style={{ color: colors.text }}
          className="text-lg font-bold flex-1 text-center mr-8"
          numberOfLines={1}
        >
          Opdracht
        </Text>
      </View>

      <ScrollView
        className="flex-1"
        refreshControl={
          <RefreshControl
            refreshing={isRefreshing}
            onRefresh={handleRefresh}
            tintColor={colors.primary}
            colors={[colors.primary]}
          />
        }
      >
        {/* Hero Section */}
        <View
          className="mx-4 mt-4 rounded-2xl p-6"
          style={{ backgroundColor: "#6366F115" }}
        >
          <View className="flex-row items-start">
            <View
              className="w-16 h-16 rounded-2xl items-center justify-center"
              style={{ backgroundColor: "#6366F130" }}
            >
              <Ionicons name="list" size={32} color="#6366F1" />
            </View>

            <View className="flex-1 ml-4">
              <Text className="text-xl font-bold" style={{ color: colors.text }}>
                {opdracht.title}
              </Text>

              {opdracht.description && (
                <Text className="mt-1" style={{ color: colors.textSecondary }}>
                  {opdracht.description}
                </Text>
              )}

              {/* Progress */}
              <View className="mt-3">
                <View className="flex-row items-center justify-between mb-1">
                  <Text className="text-sm" style={{ color: colors.textSecondary }}>
                    Voortgang
                  </Text>
                  <Text className="text-sm font-medium" style={{ color: colors.text }}>
                    {opdracht.completed_sections}/{opdracht.total_sections} secties
                  </Text>
                </View>
                <View
                  className="h-2 rounded-full overflow-hidden"
                  style={{ backgroundColor: colors.surfaceSecondary }}
                >
                  <View
                    className="h-full rounded-full"
                    style={{
                      width: `${opdracht.progress_percentage}%`,
                      backgroundColor: "#6366F1",
                    }}
                  />
                </View>
              </View>

              {/* Points */}
              <View className="flex-row items-center mt-3">
                <Ionicons name="star" size={16} color="#F59E0B" />
                <Text className="ml-1 font-medium" style={{ color: "#F59E0B" }}>
                  {opdracht.earned_points}/{opdracht.total_points} punten
                </Text>
              </View>
            </View>
          </View>
        </View>

        {/* Explainer */}
        {config.explainer && (
          <View
            className="mx-4 mt-4 p-4 rounded-xl"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            <View className="flex-row items-center mb-2">
              <Ionicons name="information-circle" size={20} color={colors.primary} />
              <Text className="ml-2 font-semibold" style={{ color: colors.text }}>
                Uitleg
              </Text>
            </View>
            <Text style={{ color: colors.textSecondary }}>{config.explainer}</Text>
          </View>
        )}

        {/* Sections */}
        <View className="mx-4 mt-4 mb-8">
          <Text className="font-bold text-lg mb-3" style={{ color: colors.text }}>
            Secties
          </Text>

          {opdracht.sections.map((section, index) => {
            const isLocked = config.require_order && index > 0 && !opdracht.sections[index - 1].is_completed;
            const isPending = section.completion?.status === "pending";

            return (
              <TouchableOpacity
                key={section.id}
                onPress={() => {
                  if (!isLocked && !section.is_completed && !isPending) {
                    setActiveSection(section);
                  }
                }}
                disabled={isLocked || section.is_completed || isPending}
                className="rounded-xl p-4 mb-2 flex-row items-center"
                style={{
                  backgroundColor: section.is_completed
                    ? colors.success + "10"
                    : isPending
                    ? "#F59E0B10"
                    : colors.surface,
                  borderWidth: 1,
                  borderColor: section.is_completed
                    ? colors.success
                    : isPending
                    ? "#F59E0B"
                    : colors.border,
                  opacity: isLocked ? 0.5 : 1,
                }}
              >
                {/* Status Icon */}
                <View
                  className="w-10 h-10 rounded-lg items-center justify-center mr-3"
                  style={{
                    backgroundColor: section.is_completed
                      ? colors.success + "20"
                      : isPending
                      ? "#F59E0B20"
                      : isLocked
                      ? colors.surfaceSecondary
                      : SECTION_COLORS[section.section_type] + "20",
                  }}
                >
                  {section.is_completed ? (
                    <Ionicons name="checkmark-circle" size={24} color={colors.success} />
                  ) : isPending ? (
                    <Ionicons name="time" size={24} color="#F59E0B" />
                  ) : isLocked ? (
                    <Ionicons name="lock-closed" size={24} color={colors.textTertiary} />
                  ) : (
                    <Ionicons
                      name={SECTION_ICONS[section.section_type] as any}
                      size={24}
                      color={SECTION_COLORS[section.section_type]}
                    />
                  )}
                </View>

                {/* Content */}
                <View className="flex-1">
                  <Text className="font-medium" style={{ color: colors.text }}>
                    {index + 1}. {section.title}
                  </Text>
                  <View className="flex-row items-center mt-1">
                    <Text className="text-sm" style={{ color: colors.textSecondary }}>
                      {SECTION_LABELS[section.section_type]}
                    </Text>
                    <Text className="text-sm mx-2" style={{ color: colors.textTertiary }}>
                      •
                    </Text>
                    <Ionicons name="star" size={12} color="#F59E0B" />
                    <Text className="text-sm ml-1" style={{ color: "#F59E0B" }}>
                      {section.is_completed ? section.completion?.points_earned : section.points} pts
                    </Text>
                    {!section.is_required && (
                      <>
                        <Text className="text-sm mx-2" style={{ color: colors.textTertiary }}>
                          •
                        </Text>
                        <Text className="text-sm" style={{ color: colors.textTertiary }}>
                          Optioneel
                        </Text>
                      </>
                    )}
                  </View>
                </View>

                {/* Arrow / Status */}
                {!section.is_completed && !isPending && !isLocked && (
                  <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
                )}
                {isPending && (
                  <View className="px-2 py-1 rounded-full" style={{ backgroundColor: "#F59E0B20" }}>
                    <Text className="text-xs font-medium" style={{ color: "#F59E0B" }}>
                      In afwachting
                    </Text>
                  </View>
                )}
              </TouchableOpacity>
            );
          })}
        </View>
      </ScrollView>

      {/* Section completion form */}
      {activeSection && renderSectionForm()}
    </SafeAreaView>
  );
}
